'use client';

import { useEffect } from 'react';

/**
 * Registers /sw.js on mount + watches for updates.
 *
 * V2.11 (2026-05-21b) — when a new SW finishes installing while an
 * old controller is active, dispatch `agentx:sw-update-ready`. The
 * <PwaUpdateToast> client component listens for that and shows a
 * "新版本可用" prompt; tap to activate via SKIP_WAITING + reload.
 *
 * No-op in dev (SW disabled to avoid stale HMR), no-op if the browser
 * doesn't support service workers.
 *
 * Returns null — pure side effect.
 */
export function ServiceWorkerRegister(): null {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV !== 'production') return;

    let cancelled = false;

    function watchForUpdate(reg: ServiceWorkerRegistration) {
      // If there's already a waiting worker by the time we register
      // (e.g. user reopened the tab and the prior session installed
      // it), surface the prompt immediately.
      if (reg.waiting && navigator.serviceWorker.controller) {
        window.dispatchEvent(
          new CustomEvent('agentx:sw-update-ready', { detail: { registration: reg } }),
        );
      }
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // Only prompt when a brand-new worker takes the 'installed'
          // state AND there's already an active controller — i.e.
          // we're upgrading an existing PWA, not the first install.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            window.dispatchEvent(
              new CustomEvent('agentx:sw-update-ready', { detail: { registration: reg } }),
            );
          }
        });
      });
    }

    async function register() {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        if (cancelled) return;
        watchForUpdate(reg);
      } catch {
        // swallow — SW registration failures shouldn't break the page
      }
    }

    // After a SKIP_WAITING activate-and-claim, refresh once so the new
    // bundle serves on the next request. Guard against the legacy
    // browsers that fire controllerchange twice during initial install.
    let didReload = false;
    function onControllerChange() {
      if (didReload) return;
      didReload = true;
      window.location.reload();
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    if (document.readyState === 'complete') {
      void register();
    } else {
      window.addEventListener('load', () => void register(), { once: true });
    }

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  return null;
}
